import React from 'react';
import { Button, Checkbox, Input, InputNumber, Radio, Select, Space } from 'antd';
import { CloseCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { ScreenQuestionCardLayout } from './ScreenQuestionCardLayout';

type QuestionType = 'Text' | 'MCQ' | 'YesNo' | 'Numeric' | 'SalaryRange' | 'Salary';

type Answer = { id: string; label: string; points?: number };

export type CustomQuestionValue = {
  type: QuestionType;
  title?: string;
  required?: boolean;
  idealAnswer?: string;
  answers?: Answer[];
  expectedYesNo?: 'Yes' | 'No';
  expectedNumber?: number;
};

type Props = {
  value?: CustomQuestionValue;
  onChange?: (v: CustomQuestionValue) => void;
  onRemove?: () => void;
  className?: string;
  title?: string;
};

const typeOptions = [
  { label: 'Text', value: 'Text' },
  { label: 'MCQ', value: 'MCQ' },
  { label: 'Yes | No', value: 'YesNo' },
  { label: 'Numeric', value: 'Numeric' },
];

const uid = () => Math.random().toString(36).slice(2, 9);

export const CustomQuestionCard: React.FC<Props> = ({
  value,
  onChange,
  onRemove,
  className = '',
  title,
}) => {
  const v: CustomQuestionValue = {
    type: 'Text',
    required: false,
    answers: [
      { id: uid(), label: '' },
      { id: uid(), label: '' },
    ],
    ...value,
  };

  const set = (patch: Partial<CustomQuestionValue>) => onChange?.({ ...v, ...patch });

  const updateAnswer = (id: string, patch: Partial<Answer>) => {
    const next = (v.answers ?? []).map((a) => (a.id === id ? { ...a, ...patch } : a));
    set({ answers: next });
  };

  const addAnswer = () => set({ answers: [...(v.answers ?? []), { id: uid(), label: '' }] });

  const removeAnswer = (id: string) =>
    set({ answers: (v.answers ?? []).filter((a) => a.id !== id) });

  return (
    // <Card
    //   bordered
    //   className={`rounded-2xl ${className}`}
    //   title={<span className="font-medium">{title ?? 'Custom Question'}</span>}
    //   extra={
    //     <Popconfirm
    //       title="Delete this item?"
    //       description="This action cannot be undone."
    //       okText="Delete"
    //       cancelText="Cancel"
    //       onConfirm={onRemove}
    //       okButtonProps={{ danger: true }}
    //       placement="left"
    //     >
    //       <Tooltip title="Delete">
    //         <DeleteOutlined className="text-gray-400 hover:text-red-500 cursor-pointer" />
    //       </Tooltip>
    //     </Popconfirm>
    //   }>
    <ScreenQuestionCardLayout title={'Custom Question'} onDelete={onRemove} className="border">
      <div className={`relative bg-white px-4 pt-1 ${className}`}>
        {/*<div className="absolute right-3 top-3">*/}
        {/*  {onRemove && (*/}
        {/*    <Popconfirm*/}
        {/*      title="Delete this item?"*/}
        {/*      description="This action cannot be undone."*/}
        {/*      okText="Delete"*/}
        {/*      cancelText="Cancel"*/}
        {/*      onConfirm={() => console.log('delete')}*/}
        {/*      okButtonProps={{ danger: true }}*/}
        {/*      placement="left">*/}
        {/*      <Tooltip title="Delete">*/}
        {/*        <DeleteOutlined className="text-gray-400 hover:text-red-500 cursor-pointer" />*/}
        {/*      </Tooltip>*/}
        {/*    </Popconfirm>*/}
        {/*  )}*/}
        {/*</div>*/}

        <Space.Compact className="w-full">
          <Select
            className="!w-36"
            options={typeOptions}
            value={v.type}
            onChange={(t: QuestionType) => set({ type: t })}
          />
          <Input
            placeholder="Type your question here"
            value={v.title}
            onChange={(e) => set({ title: e.target.value })}
          />
        </Space.Compact>

        {v.type === 'Text' && (
          <div className="mt-3">
            <Input
              placeholder="Enter ideal answer"
              value={v.idealAnswer}
              onChange={(e) => set({ idealAnswer: e.target.value })}
            />
          </div>
        )}

        {v.type === 'MCQ' && (
          <div className="mt-3">
            {(v.answers ?? []).map((a, idx) => (
              <div key={a.id} className="mb-2 flex items-center gap-2">
                <Input
                  placeholder={`Answer ${idx + 1}`}
                  value={a.label}
                  onChange={(e) => updateAnswer(a.id, { label: e.target.value })}
                />
                <Select
                  className="!w-32"
                  placeholder="Points"
                  value={a.points}
                  onChange={(pts) => updateAnswer(a.id, { points: pts })}
                  options={[0, 1, 2, 3, 4, 5].map((n) => ({ label: `${n} Points`, value: n }))}
                  suffixIcon={<span className="text-gray-400">▾</span>}
                />
                <Button
                  style={{ backgroundColor: '#F1F2F4', color: 'black' }}
                  type="primary"
                  icon={<CloseCircleOutlined />}
                  onClick={() => removeAnswer(a.id)}
                />
              </div>
            ))}
            <div className="mt-3 flex items-center justify-center text-sm">
              <Button
                className="text-sm"
                type="text"
                icon={<PlusCircleOutlined />}
                onClick={addAnswer}>
                Add New Answer
              </Button>
            </div>
          </div>
        )}

        {v.type === 'YesNo' && (
          <div className="mt-3">
            <div className="text-base text-gray-500 mb-2">
              <span className="text-red-500">*</span> Expected Answer
            </div>
            <Radio.Group
              value={v.expectedYesNo}
              onChange={(e) => set({ expectedYesNo: e.target.value })}>
              <Space size="large">
                <Radio value="Yes">Yes</Radio>
                <Radio value="No">No</Radio>
              </Space>
            </Radio.Group>
          </div>
        )}

        {v.type === 'Numeric' && (
          <div className="mt-3">
            <div className=" mb-1 text-xs font-medium">
              <span className="!text-red-500">*</span> Expected Answer
            </div>
            <Space.Compact className="!w-48">
              {/*<Select*/}
              {/*  disabled*/}
              {/*  defaultValue="Number"*/}
              {/*  options={[{ label: 'Number', value: 'Number' }]}*/}
              {/*/>*/}
              <InputNumber
                className="!w-full"
                placeholder="Number"
                value={v.expectedNumber}
                onChange={(n) => set({ expectedNumber: n ?? undefined })}
              />
            </Space.Compact>
          </div>
        )}

        {/*<Divider className="my-3" />*/}

        <Checkbox
          className="mt-4 ml-3 [&>span]:text-base"
          checked={!!v.required}
          onChange={(e) => set({ required: e.target.checked })}>
          Required
        </Checkbox>
      </div>
    </ScreenQuestionCardLayout>
    // </Card>
  );
};

// import React from 'react';
// import {
//   Button,
//   Card,
//   Checkbox,
//   Divider,
//   Input,
//   InputNumber,
//   Popconfirm,
//   Radio,
//   Select,
//   Space,
//   Tooltip,
// } from 'antd';
// import { DeleteOutlined, PlusOutlined, CloseOutlined } from '@ant-design/icons';
//
// type QuestionType =
//   | 'Text'
//   | 'YesNo'
//   | 'Mcq'
//   | 'Number'
//   | 'Select'
//   | 'Salary'
//   | 'SalaryRange'
//   | 'WorkExperience'
//   | 'Skills'
//   | 'Language'
//   | 'NoticePeriod';
//
// type Answer = { id: string; label: string; points?: number };
//
// export type CustomQuestionValue = {
//   type: QuestionType;
//   title?: string;
//   required?: boolean;
//   idealAnswer?: string;
//   answers?: Answer[];
//   expectedYesNo?: 'Yes' | 'No';
//   expectedNumber?: number;
// };
//
// type Props = {
//   value?: CustomQuestionValue;
//   onChange?: (v: CustomQuestionValue) => void;
//   onRemove?: () => void;
//   className?: string;
//   title?: string;
//
//   preset?: {
//     type: QuestionType;
//     question: string;
//     cardTitle?: string;
//   };
// };
//
// const typeOptions = [
//   { label: 'Text', value: 'Text' },
//   { label: 'MCQ', value: 'MCQ' },
//   { label: 'Yes | No', value: 'YesNo' },
//   { label: 'Numeric', value: 'Numeric' },
// ];
//
// const uid = () => Math.random().toString(36).slice(2, 9);
//
// export const CustomQuestionCard: React.FC<Props> = ({
//   value,
//   onChange,
//   onRemove,
//   className = '',
//   title,
//   preset,
// }) => {
//   const locked = !!preset;
//
//   const v: CustomQuestionValue = {
//     type: locked ? preset!.type : 'Text',
//     required: false,
//     // If preset exists, show its question as the value.title
//     title: locked ? preset!.question : value?.title,
//     answers: [
//       { id: uid(), label: '' },
//       { id: uid(), label: '' },
//     ],
//     ...value,
//     // Ensure type is overridden by preset when locked
//     ...(locked ? { type: preset!.type } : {}),
//   };
//
//   const set = (patch: Partial<CustomQuestionValue>) => onChange?.({ ...v, ...patch });
//
//   const updateAnswer = (id: string, patch: Partial<Answer>) => {
//     const next = (v.answers ?? []).map((a) => (a.id === id ? { ...a, ...patch } : a));
//     set({ answers: next });
//   };
//
//   const addAnswer = () => set({ answers: [...(v.answers ?? []), { id: uid(), label: '' }] });
//
//   const removeAnswer = (id: string) =>
//     set({ answers: (v.answers ?? []).filter((a) => a.id !== id) });
//
//   const cardTitle = preset?.cardTitle ?? title ?? 'Custom Question';
//
//   return (
//     <Card
//       bordered
//       className={`rounded-2xl ${className}`}
//       title={<span className="font-medium">{cardTitle}</span>}
//       extra={
//         onRemove ? (
//           <Popconfirm
//             title="Delete this item?"
//             description="This action cannot be undone."
//             okText="Delete"
//             cancelText="Cancel"
//             onConfirm={onRemove}
//             okButtonProps={{ danger: true }}
//             placement="left">
//             <Tooltip title="Delete">
//               <DeleteOutlined className="text-gray-400 hover:text-red-500 cursor-pointer" />
//             </Tooltip>
//           </Popconfirm>
//         ) : null
//       }>
//       <div
//         className={`relative rounded-2xl border border-gray-200 bg-white p-4 shadow-sm ${className}`}>
//         {/* Top row: either locked question text or editable type+question */}
//         {locked ? (
//           <div className="flex flex-col">
//             <div className="text-xs text-gray-500 mb-1">Question Type</div>
//             <div className="text-sm font-medium mb-2">{v.type}</div>
//             <div className="text-xs text-gray-500 mb-1">Question</div>
//             <div className="text-base">{v.title}</div>
//           </div>
//         ) : (
//           <Space.Compact className="w-full">
//             <Select
//               className="!w-36"
//               options={typeOptions}
//               value={v.type}
//               onChange={(t: QuestionType) => set({ type: t })}
//             />
//             <Input
//               placeholder="Type your question here"
//               value={v.title}
//               onChange={(e) => set({ title: e.target.value })}
//             />
//           </Space.Compact>
//         )}
//
//         {/* Body per type */}
//         {v.type === 'Text' && !locked && (
//           <div className="mt-3">
//             <Input
//               placeholder="Enter ideal answer"
//               value={v.idealAnswer}
//               onChange={(e) => set({ idealAnswer: e.target.value })}
//             />
//           </div>
//         )}
//
//         {v.type === 'MCQ' && (
//           <div className="mt-3">
//             {(v.answers ?? []).map((a, idx) => (
//               <div key={a.id} className="mb-2 flex items-center gap-2">
//                 <Input
//                   placeholder={`Answer ${idx + 1}`}
//                   value={a.label}
//                   onChange={(e) => updateAnswer(a.id, { label: e.target.value })}
//                 />
//                 <Select
//                   className="!w-28"
//                   placeholder="Points"
//                   value={a.points}
//                   onChange={(pts) => updateAnswer(a.id, { points: pts })}
//                   options={[0, 1, 2, 3, 4, 5].map((n) => ({
//                     label: `${n} Points`,
//                     value: n,
//                   }))}
//                   suffixIcon={<span className="text-gray-400">▾</span>}
//                 />
//                 <Button type="text" icon={<CloseOutlined />} onClick={() => removeAnswer(a.id)} />
//               </div>
//             ))}
//
//             <div className="mt-3 flex items-center justify-center">
//               <Button type="dashed" icon={<PlusOutlined />} onClick={addAnswer}>
//                 Add New Answer
//               </Button>
//             </div>
//           </div>
//         )}
//
//         {v.type === 'YesNo' && (
//           <div className="mt-3">
//             <div className="text-red-500 mb-1 text-xs font-medium">* Expected Answer</div>
//             <Radio.Group
//               value={v.expectedYesNo}
//               onChange={(e) => set({ expectedYesNo: e.target.value })}>
//               <Space size="large">
//                 <Radio value="Yes">Yes</Radio>
//                 <Radio value="No">No</Radio>
//               </Space>
//             </Radio.Group>
//           </div>
//         )}
//
//         {v.type === 'Numeric' && (
//           <div className="mt-3">
//             <div className="text-red-500 mb-1 text-xs font-medium">* Expected Answer</div>
//             <Space.Compact className="!w-48">
//               <Select
//                 disabled
//                 defaultValue="Number"
//                 options={[{ label: 'Number', value: 'Number' }]}
//               />
//               <InputNumber
//                 className="!w-full"
//                 placeholder="Number"
//                 value={v.expectedNumber}
//                 onChange={(n) => set({ expectedNumber: n ?? undefined })}
//               />
//             </Space.Compact>
//           </div>
//         )}
//
//         <Divider className="my-3" />
//
//         <Checkbox checked={!!v.required} onChange={(e) => set({ required: e.target.checked })}>
//           Required
//         </Checkbox>
//       </div>
//     </Card>
//   );
// };
